import React from 'react';
import { motion } from 'framer-motion';
import { BriefcaseIcon, UserGroupIcon, ChartBarIcon, HandRaisedIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import img5 from '../assets/5.jpg'
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
const AboutPage = () => {
  const values = [
    {
      title: "Professional Integrity",
      description: "Uncompromising ethical standards in all our engagements",
      icon: HandRaisedIcon,
      color: "bg-blue-100"
    },
    {
      title: "Client-Centric Approach",
      description: "Tailored solutions for unique business needs",
      icon: UserGroupIcon,
      color: "bg-purple-100"
    },
    {
      title: "Technical Excellence",
      description: "Cutting-edge expertise in financial governance",
      icon: ChartBarIcon,
      color: "bg-green-100"
    },
    {
      title: "Strategic Partnership",
      description: "Long-term relationships driving business success",
      icon: BriefcaseIcon,
      color: "bg-orange-100"
    }
  ];

  const team = [
    { name: "Rahul Sharma", role: "Managing Partner", exp: "18+ years" },
    { name: "Priya Patel", role: "Tax Director", exp: "15+ years" },
    { name: "Arjun Singh", role: "Audit Head", exp: "12+ years" },
    { name: "Neha Gupta", role: "GST Specialist", exp: "10+ years" }
  ];

  const milestones = [
    { year: "2010", title: "Founded in Mumbai", description: "Started with 5-member team" },
    { year: "2014", title: "Expanded Services", description: "Added GST and Compliance divisions" },
    { year: "2018", title: "National Presence", description: "Opened 3 new branches across India" },
    { year: "2023", title: "Tech Transformation", description: "Implemented AI-driven audit systems" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header (Same as Services Page) */}
      <Header />

      {/* Hero Section */}
      <div className="relative pt-32 pb-24 md:py-48 bg-gradient-to-r from-customBlueDark to-customBlueLight">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight"
          >
            Trusted Financial Leadership 
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xl text-blue-100 max-w-3xl mx-auto"
          >
            Partnering with businesses to navigate complex financial landscapes through innovative solutions and expert guidance.
          </motion.p>
        </div>
      </div>

      {/* About Content */}
      <div className="container mx-auto px-4 lg:px-8 py-16 md:py-24">
        {/* Mission Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid lg:grid-cols-2 gap-12 mb-24"
        >
          <div className="relative h-96 rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 opacity-20" />
            <img 
              src={img5}
              alt="Team Discussion"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Strategic Financial Architects
            </h2>
            <p className="text-gray-600 text-lg mb-6">
              At Our Firm, we combine traditional financial wisdom with modern innovation to create sustainable growth strategies. Our team of certified professionals brings together decades of experience across diverse industries.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 bg-white rounded-xl shadow-sm">
                <h3 className="text-2xl font-bold text-blue-600 mb-2">1500+</h3>
                <p className="text-gray-600">Successful Projects</p>
              </div>
              <div className="p-6 bg-white rounded-xl shadow-sm">
                <h3 className="text-2xl font-bold text-blue-600 mb-2">98%</h3>
                <p className="text-gray-600">Client Retention Rate</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Core Values */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
            Our Core Values
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                whileHover={{ y: -5 }}
                className="p-8 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className={`${value.color} w-12 h-12 rounded-lg flex items-center justify-center mb-6`}>
                  <value.icon className="w-6 h-6 text-gray-800" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Our Process */}
<motion.div 
  initial={{ opacity: 0 }}
  whileInView={{ opacity: 1 }}
  viewport={{ once: true }}
  className="mb-24"
>
  <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
    Our Engagement Process
  </h2>
  <div className="grid md:grid-cols-4 gap-6">
    <div className="text-center p-6">
      <div className="w-16 h-16 bg-customBlueDark text-white rounded-full flex items-center justify-center mx-auto mb-4">
        1
      </div>
      <h3 className="text-lg font-semibold mb-2">Discovery</h3>
      <p className="text-gray-600 text-sm">Needs assessment & goal alignment</p>
    </div>
    
    <div className="text-center p-6">
      <div className="w-16 h-16 bg-customBlueDark text-white rounded-full flex items-center justify-center mx-auto mb-4">
        2
      </div>
      <h3 className="text-lg font-semibold mb-2">Strategy Design</h3>
      <p className="text-gray-600 text-sm">Custom solution development</p>
    </div>

    <div className="text-center p-6">
      <div className="w-16 h-16 bg-customBlueDark text-white rounded-full flex items-center justify-center mx-auto mb-4">
        3
      </div>
      <h3 className="text-lg font-semibold mb-2">Implementation</h3>
      <p className="text-gray-600 text-sm">Seamless execution & integration</p>
    </div>

    <div className="text-center p-6">
      <div className="w-16 h-16 bg-customBlueDark text-white rounded-full flex items-center justify-center mx-auto mb-4">
        4
      </div>
      <h3 className="text-lg font-semibold mb-2">Optimization</h3>
      <p className="text-gray-600 text-sm">Continuous improvement cycle</p>
    </div>
  </div>
</motion.div>

        {/* Leadership Team */}
        {/* <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
            Leadership Team
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="group text-center">
                <div className="relative mb-6 rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-600/30 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <img 
                    src={`/api/placeholder/400x500?${index}`}
                    alt={member.name}
                    className="w-full h-80 object-cover"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">{member.name}</h3>
                <p className="text-gray-600 mb-2">{member.role}</p>
                <p className="text-sm text-blue-600">{member.exp} Experience</p>
              </div>
            ))}
          </div>
        </motion.div> */}

        {/* Timeline */}
        {/* <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
            Our Journey
          </h2>
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute left-1/2 w-1 bg-gray-200 h-full -translate-x-1/2" />
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`relative flex ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                  <div className={`w-1/2 p-6 bg-white rounded-xl shadow-lg ${index % 2 === 0 ? 'ml-6' : 'mr-6'}`}>
                    <div className="absolute top-6 w-4 h-4 bg-blue-600 rounded-full -translate-x-1/2 left-1/2" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{milestone.year}</h3>
                    <h4 className="text-lg font-medium text-blue-600 mb-2">{milestone.title}</h4>
                    <p className="text-gray-600">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div> */}
        <motion.div 
  initial={{ opacity: 0 }}
  whileInView={{ opacity: 1 }}
  viewport={{ once: true }}
  className="mb-24"
>
  <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
    Client Success Stories
  </h2>
  <div className="grid md:grid-cols-2 gap-8">
    <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
      <div className="flex items-center mb-4">
        <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
          <BriefcaseIcon className="w-8 h-8 text-blue-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold">Manufacturing Turnaround</h3>
          <p className="text-gray-600">Auto Components Manufacturer</p>
        </div>
      </div>
      <p className="text-gray-600 mb-4">
        Revitalized financial operations for 200+ Cr company, reducing compliance costs by 40% while improving audit efficiency.
      </p>
      <div className="flex justify-between text-sm">
        <div>
          <p className="font-semibold">35%</p>
          <p className="text-gray-600">Cost Reduction</p>
        </div>
        <div>
          <p className="font-semibold">100%</p>
          <p className="text-gray-600">Compliance Accuracy</p>
        </div>
      </div>
    </div>

    <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
      <div className="flex items-center mb-4">
        <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mr-4">
          <ChartBarIcon className="w-8 h-8 text-green-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold">Tech Startup Scaling</h3>
          <p className="text-gray-600">SaaS Platform</p>
        </div>
      </div>
      <p className="text-gray-600 mb-4">
        Enabled 500% growth through strategic tax planning and investor-ready financial structuring.
      </p>
      <div className="flex justify-between text-sm">
        <div>
          <p className="font-semibold">₹50Cr+</p>
          <p className="text-gray-600">Funds Raised</p>
        </div>
        <div>
          <p className="font-semibold">3 Years</p>
          <p className="text-gray-600">Sustained Growth</p>
        </div>
      </div>
    </div>
  </div>
</motion.div>



{/* Client Testimonials */}
{/* <motion.div 
  initial={{ opacity: 0 }}
  whileInView={{ opacity: 1 }}
  viewport={{ once: true }}
  className="mb-24"
>
  <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
    Client Testimonials
  </h2>
  <div className="grid md:grid-cols-2 gap-8">
    <div className="bg-white p-8 rounded-2xl shadow-lg">
      <div className="flex items-center mb-4">
        <img 
          src="/api/placeholder/100x100" 
          alt="Client" 
          className="w-12 h-12 rounded-full mr-4"
        />
        <div>
          <h4 className="font-semibold">Rajesh Mehta</h4>
          <p className="text-gray-600 text-sm">CFO, TechCorp India</p>
        </div>
      </div>
      <p className="text-gray-600">
        "Their strategic tax planning helped us save ₹2.8Cr annually while maintaining full compliance. Truly partners in our growth journey."
      </p>
    </div>

    <div className="bg-white p-8 rounded-2xl shadow-lg">
      <div className="flex items-center mb-4">
        <img 
          src="/api/placeholder/100x100" 
          alt="Client" 
          className="w-12 h-12 rounded-full mr-4"
        />
        <div>
          <h4 className="font-semibold">Anjali Deshpande</h4>
          <p className="text-gray-600 text-sm">Founder, GreenSolutions</p>
        </div>
      </div>
      <p className="text-gray-600">
        "From startup to scale-up, their financial guidance has been instrumental in our successful funding rounds and operational efficiency."
      </p>
    </div>
  </div>
</motion.div> */}

        {/* CTA Section */}

        <div className="bg-customBlueLight rounded-2xl p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Financial Strategy?
          </h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            className="bg-white text-customBlueDark py-4 px-12 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
          >
             <Link to="/contact" className="block w-full h-full">Schedule Consultation</Link>
          </motion.button>
        </div>
      </div>

     <Footer />
    </div>
  );
};

export default AboutPage;